import type { CaseListItem } from "@/domain/case/case";
import type { CaseHistoryEvent } from "@/features/case-detail/use-case-detail";

/** 1 案件とその履歴。 */
export type CaseWithEvents = {
  readonly case: CaseListItem;
  readonly events: readonly CaseHistoryEvent[];
};

export type ActivityEntry = {
  readonly id: number;
  readonly caseId: number;
  readonly caseNumber: string;
  readonly processModelId: string;
  readonly processModelDisplayName: string;
  readonly taskName: string | null;
  readonly eventType: string;
  readonly actor: string;
  readonly occurredAt: Date;
};

export type ActivityFilters = {
  /** 実行者の部分一致。 */
  readonly actor?: string;
  /** YYYY-MM-DD。境界日を含む。 */
  readonly from?: string;
  readonly to?: string;
};

/**
 * 案件ごとの履歴を横断の操作履歴に束ねる。
 *
 * 人の操作だけを残す（`username` が無いイベントはスクリプトタスク等のエンジン内部の
 * 遷移で、「誰が何をしたか」の記録にはならない）。日時が取れないものも落とす。
 *
 * 承認か差し戻しかの区別は付けられない。判定値はタスクのフォームデータ側にあり
 * process_instance_event には乗らないため、内訳は案件詳細で確認する。
 */
export function buildActivityEntries(
  source: readonly CaseWithEvents[],
  filters: ActivityFilters = {},
): readonly ActivityEntry[] {
  const actorNeedle = filters.actor?.trim().toLowerCase() ?? "";
  const from = parseBoundary(filters.from, "start");
  const to = parseBoundary(filters.to, "end");

  const entries: ActivityEntry[] = [];

  for (const { case: caseItem, events } of source) {
    for (const event of events) {
      if (event.actor === null || event.occurredAt === null) continue;
      if (
        actorNeedle !== "" &&
        !event.actor.toLowerCase().includes(actorNeedle)
      ) {
        continue;
      }
      if (from !== null && event.occurredAt.getTime() < from) continue;
      if (to !== null && event.occurredAt.getTime() > to) continue;

      entries.push({
        id: event.id,
        caseId: caseItem.caseId,
        caseNumber: caseItem.caseNumber,
        processModelId: caseItem.processModelId,
        processModelDisplayName: caseItem.processModelDisplayName,
        taskName: event.taskName,
        eventType: event.eventType,
        actor: event.actor,
        occurredAt: event.occurredAt,
      });
    }
  }

  return entries.sort(
    (a, b) => b.occurredAt.getTime() - a.occurredAt.getTime() || b.id - a.id,
  );
}

/**
 * 日付だけの指定を、その日の始点／終点として解釈する。
 *
 * `<input type="date">` の値は利用者のローカル日なので**ローカル時刻**で区切る
 * （`Z` を付けて UTC で区切ると、JST では 0〜9 時の操作が前日扱いになって
 * 画面に見えている日付と絞り込み結果がずれる）。
 */
function parseBoundary(
  value: string | undefined,
  edge: "start" | "end",
): number | null {
  if (value === undefined || value === "") return null;

  const parts = value.split("-").map((part) => Number.parseInt(part, 10));
  const [year, month, day] = parts;
  if (parts.length !== 3 || parts.some((part) => !Number.isFinite(part))) {
    return null;
  }

  const time =
    edge === "start"
      ? new Date(year!, month! - 1, day!, 0, 0, 0, 0).getTime()
      : new Date(year!, month! - 1, day!, 23, 59, 59, 999).getTime();

  return Number.isFinite(time) ? time : null;
}
